import { action, observable } from 'mobx';

function cancelledValue(kind) {
    if (kind === 'confirm') return false;
    if (kind === 'prompt') return null;
    return undefined;
}

export class DialogStore {
    @observable.shallow requests = [];
    @observable blocked = false;
    restoreFocus = true;
    host = null;
    nextId = 0;
    contextVersion = 0;
    pendingActions = new WeakMap();

    @action
    beginAction(owner, key) {
        let actions = this.pendingActions.get(owner);
        if (!actions) {
            actions = new Map();
            this.pendingActions.set(owner, actions);
        }
        if (actions.has(key)) return null;
        const token = {};
        actions.set(key, token);
        return token;
    }

    @action
    finishAction(owner, key, token) {
        const actions = this.pendingActions.get(owner);
        if (actions?.get(key) === token) actions.delete(key);
    }

    get current() {
        return this.requests.length > 0 ? this.requests[0] : null;
    }

    @action
    attach(host) {
        if (this.host && this.host !== host) this.cancelAll();
        this.host = host;
    }

    @action
    detach(host) {
        if (this.host !== host) return;
        this.host = null;
        this.cancelAll();
    }

    @action
    setBlocked(blocked) {
        this.blocked = blocked;
        if (blocked) this.cancelAll();
    }

    @action
    request(kind, message, defaultValue = '', options = {}) {
        if (!this.host || this.blocked) {
            if (!this.host) console.warn('Dialog requested without a mounted dialog host.');
            return Promise.resolve(cancelledValue(kind));
        }
        const presentation = {
            title: options.title,
            confirmLabel: options.confirmLabel,
            cancelLabel: options.cancelLabel,
        };
        const id = ++this.nextId;
        this.restoreFocus = true;
        return new Promise(resolve => {
            this.requests.push({
                id,
                kind,
                message: String(message ?? ''),
                defaultValue: String(defaultValue ?? ''),
                ...presentation,
                resolve,
            });
        });
    }

    @action
    respond(id, value) {
        const request = this.current;
        if (!request || request.id !== id) return;
        this.requests.shift();
        request.resolve(value);
    }

    @action
    dismiss(id) {
        const request = this.current;
        if (!request || request.id !== id) return;
        this.respond(id, cancelledValue(request.kind));
    }

    @action
    cancelAll() {
        this.contextVersion += 1;
        this.restoreFocus = false;
        const pending = this.requests.slice();
        this.requests = [];
        pending.forEach(request => request.resolve(cancelledValue(request.kind)));
    }
}

export default new DialogStore();
